import { useCallback, useEffect, useMemo, useState } from 'react';
import isEqual from 'react-fast-compare';
import { useFormContext } from 'react-hook-form';
import {
  FilterDropdownItem,
  FilterToolbarGroup,
  FilterToolbarItemId,
} from 'src/components/Filters';
import { useFiltersHelpers } from 'src/components/FilterToolbar/useFiltersHelpers';
import { FilterToolbarEmbeddedProps } from 'src/components/FilterToolbarEmbedded';
import { useFilterQueryContext } from 'src/contexts/FilterQueryContext';
import { ReportConfigV2 } from 'src/hooks/reportConfigs';
import { ReportFilterEditability } from 'src/utils/reportsFilterUtils';
import {
  UIReportCommissionQuery,
  UIReportV2CostOfGoodsSoldQuery,
  UIReportV2ListingQuery,
  UIReportV2PurchaseOrderLineItemQuery,
  UIReportV2PurchaseQuery,
  UIReportV2SaleLineItemQuery,
  UIReportV2SaleQuery,
  UIReportV2SaleTotalCostAdjustmentsQuery,
  UIReportV2SoldTicketQuery,
  UIReportV2TicketGroupQuery,
} from 'src/WebApiController';

import {
  getEditability,
  updateFilterItemIds,
} from './ReportBuilderFilterInput.utils';

export type ReportFilterInputProps = {
  onSubmitFilter?: () => void;
  defaultQuery?: ReportConfigV2['request']['filters'];
} & Pick<
  FilterToolbarEmbeddedProps,
  | 'direction'
  | 'performerVenueFilter'
  | 'filtersListHeader'
  | 'addFilterDropdownProps'
  | 'flexWrap'
  | 'viewerMode'
  | 'disabled'
  | 'editabilityDropdownDisabled'
>;

const FILTERS_FIELD = 'request.filters';

export type ReportFilterQueryUnion =
  | UIReportV2ListingQuery
  | UIReportV2SaleQuery
  | UIReportCommissionQuery
  | UIReportV2PurchaseQuery
  | UIReportV2SaleLineItemQuery
  | UIReportV2SaleTotalCostAdjustmentsQuery
  | UIReportV2CostOfGoodsSoldQuery
  | UIReportV2PurchaseOrderLineItemQuery
  | UIReportV2SoldTicketQuery
  | UIReportV2TicketGroupQuery;

export function useReportBuilderFilterInputV2<
  TQuery extends ReportFilterQueryUnion,
>({
  onSubmitFilter,
  mandatoryFiltersToShow,
  defaultQuery,
  filters,
  ...defaultFilterToolbarProps
}: ReportFilterInputProps & {
  filters: FilterToolbarGroup[];
  mandatoryFiltersToShow: FilterToolbarItemId[];
  defaultQuery: TQuery;
}) {
  const { setValue, watch } = useFormContext<ReportConfigV2>();

  const formFilter = watch(FILTERS_FIELD);
  const hiddenFilterItemIds = watch('hiddenFilterItemIds');
  const viewableFilterItemIds = watch('viewableFilterItemIds');
  const editableFilterItemIds = watch('editableFilterItemIds');

  const [selectedFilters, setSelectedFilters] =
    useState<FilterDropdownItem[]>();

  const {
    initialQuery,
    tempQuery,
    filterQuery,
    setFilterQuery,
    setTempQuery,
    resetTempQuery,
  } = useFilterQueryContext<TQuery>();

  const onSubmitFilterHandler = useCallback(() => {
    setFilterQuery(tempQuery);
    onSubmitFilter?.();
  }, [onSubmitFilter, setFilterQuery, tempQuery]);

  useEffect(() => {
    if (formFilter != null && !isEqual(formFilter as TQuery, tempQuery)) {
      setTempQuery(formFilter as TQuery);
      setFilterQuery(formFilter as TQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    onSubmitFilterHandler();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tempQuery]);

  useEffect(() => {
    if (!isEqual(formFilter as TQuery, filterQuery)) {
      setValue(FILTERS_FIELD, filterQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterQuery]);

  const { appliedFilters } = useFiltersHelpers({
    filters,
    activeQuery: tempQuery,
    mandatoryFiltersToShow,
    initialQuery: defaultQuery,
    returnAllNonEmptyFilters: true,
  });

  const filtersWithEditability = useMemo(
    () =>
      filters.map((filterGroup) => ({
        ...filterGroup,
        items: filterGroup.items.map((filterItem) => ({
          ...filterItem,
          reportFilterEditability: getEditability(
            editableFilterItemIds,
            viewableFilterItemIds,
            hiddenFilterItemIds,
            filterItem.filterId
          ),
        })),
      })),
    [editableFilterItemIds, filters, hiddenFilterItemIds, viewableFilterItemIds]
  );

  const onEditabilityChange = useCallback(
    (filterId: string | string[], editability: ReportFilterEditability) => {
      const filterIds = Array.isArray(filterId) ? filterId : [filterId];

      const {
        newHiddenFilterItemIds,
        newViewableFilterItemIds,
        newEditableFilterItemIds,
      } = updateFilterItemIds(
        editableFilterItemIds,
        viewableFilterItemIds,
        hiddenFilterItemIds,
        filterIds,
        editability
      );

      setValue('hiddenFilterItemIds', newHiddenFilterItemIds);
      setValue('viewableFilterItemIds', newViewableFilterItemIds);
      setValue('editableFilterItemIds', newEditableFilterItemIds);
    },
    [
      editableFilterItemIds,
      hiddenFilterItemIds,
      setValue,
      viewableFilterItemIds,
    ]
  );

  const filterToolbarProps = useMemo(() => {
    return {
      onResetAll: () => {
        setTempQuery(defaultQuery);
        setFilterQuery(defaultQuery);
      },
      filters: filtersWithEditability,
      appliedFilterIds: appliedFilters,
      mandatoryFiltersToShow: mandatoryFiltersToShow,
      resetTempQuery,
      tempQuery,
      onEditabilityChange,
      selectedFilters,
      setSelectedFilters,
      initialQuery: defaultQuery,
      ...defaultFilterToolbarProps,
    } as FilterToolbarEmbeddedProps;
  }, [
    filtersWithEditability,
    appliedFilters,
    mandatoryFiltersToShow,
    defaultFilterToolbarProps,
    defaultQuery,
    setTempQuery,
    setFilterQuery,
    resetTempQuery,
    tempQuery,
    onEditabilityChange,
    selectedFilters,
    setSelectedFilters,
  ]);

  return {
    filterToolbarProps,
  };
}
